import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Drawer,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter
} from "@/components/ui/drawer";
import { AppDrawerContent } from "@/components/layouts/AppDrawerContent";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { FormFieldNewType } from "@/components/controls/Form/types";
import { Close } from "@dreamhub/designsystem/icons";
import { DrawerTitle as CustomDrawerTitle } from "../../FieldManagement/components/DrawerTitle";
import { getFieldDisplayTypeIcon } from "../../FieldManagement/hooks";
import { FieldDisplayType } from "../../FieldManagement/types";
import { generateFieldDisplayTypeOptions } from "../../FieldManagement/utils";
import { APIFieldItem } from "@/pages/settings/types";
import AddActionButton from "@/components/ui/add-action-button";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import { WorkflowRequiredFieldSection } from "./components/WorkflowRequiredFieldSection";
import { AIAutofillSection } from "./components/AIAutofillSection";
import { QuestionnaireType } from "@/components/controls/Questionnaires/Questionnaire";
import { defaultFieldDisplayTypeByWorkflowType } from "@/pages/settings/components/dealsSettings/StageStudio/workflow/AddSimpleWorkflowFieldDrawer";
import { DreamerFlow } from "./hooks/useAutofillApi";
import {
  SystemFieldReplacement,
  useSystemFieldReplacement
} from "./hooks/useSystemFieldReplacement";
import { AUTOFILL_TRANSLATION_KEY } from "./const";
import { workflowFieldHasEdits } from "./helpers";

const TRANSLATION_KEY_TABS = "settings.deals";
const TRANSLATION_PREFIX = "settings.deals.workflow.editSimpleField";

export type SimpleWorkflowFieldSaveData = APIFieldItem & {
  fieldLabel: string;
  fieldHelpText: string | null;
  isRequiredForPass: boolean;
  filledByDreamer: boolean;
  autofillInstructions: string | null;
  dreamerFlow: DreamerFlow | null;
  systemFieldReplacement: SystemFieldReplacement | null;
};

interface EditSimpleWorkflowFieldDrawerProps {
  workflowType: QuestionnaireType;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  fieldData: APIFieldItem | null;
  questionnaireId: string;
  enableAutofill?: boolean;
  // Awaited, so a failed save leaves the drawer open with its draft still disposable.
  onSave?: (fieldData: SimpleWorkflowFieldSaveData) => void | Promise<void>;
  onDelete?: (fieldId: string) => void;
}

export const EditSimpleWorkflowFieldDrawer: React.FC<
  EditSimpleWorkflowFieldDrawerProps
> = ({
  workflowType,
  isOpen,
  onOpenChange,
  fieldData,
  questionnaireId,
  enableAutofill = true,
  onSave,
  onDelete
}) => {
  const { t } = useTranslation();
  const showAutofill = enableAutofill;
  const formRef = useRef<HTMLFormElement>(null);

  const [fieldLabel, setFieldLabel] = useState("");
  const [fieldHelpText, setFieldHelpText] = useState("");
  const [showHelpText, setShowHelpText] = useState(false);
  const [isRequiredForPass, setIsRequiredForPass] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteConfirmed, setIsDeleteConfirmed] = useState(false);
  const [filledByDreamer, setFilledByDreamer] = useState(false);
  const [autofillInstructions, setAutofillInstructions] = useState<
    string | null
  >(null);
  const [dreamerFlow, setDreamerFlow] = useState<DreamerFlow | null>(null);
  const [autofillSaveBlocked, setAutofillSaveBlocked] = useState(false);

  const defaultFieldDisplayType =
    defaultFieldDisplayTypeByWorkflowType[workflowType] ||
    FieldDisplayType.BOOL;

  useEffect(() => {
    if (isOpen && fieldData) {
      setFieldLabel(fieldData.label || "");
      setFieldHelpText(fieldData.helpText || "");
      setShowHelpText(!!fieldData.helpText);
      setIsRequiredForPass(fieldData.required);
      setIsFormValid(!!fieldData.label);
      setFilledByDreamer(fieldData.filledByDreamer ?? false);
      setAutofillInstructions(null);
      setDreamerFlow(null);
      setAutofillSaveBlocked(false);
    }
  }, [isOpen, fieldData]);

  const {
    promptOwner,
    onAutofillQuestionIdChange,
    replacement,
    discardDraft,
    markSaved
  } = useSystemFieldReplacement(fieldData, fieldLabel);

  const handleClose = () => {
    discardDraft();
    setFieldLabel("");
    setFieldHelpText("");
    setShowHelpText(false);
    setIsRequiredForPass(false);
    setIsFormValid(false);
    setFilledByDreamer(false);
    setAutofillInstructions(null);
    setDreamerFlow(null);
    setAutofillSaveBlocked(false);
    onOpenChange(false);
  };

  const handleDeleteClick = () => {
    setIsDeleteConfirmed(false);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (fieldData) {
      onDelete?.(fieldData.id);
      setIsDeleteModalOpen(false);
      handleClose();
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  const handleFormValid = () => {
    setIsFormValid(fieldLabel.trim().length > 0);
  };

  const handleSubmit = async () => {
    if (!isFormValid || !fieldData || !hasEdits || autofillSaveBlocked) return;

    setIsSubmitting(true);
    try {
      const updatedFieldData = {
        ...fieldData,
        fieldLabel: fieldLabel.trim(),
        fieldHelpText: fieldHelpText.trim() || null,
        isRequiredForPass,
        filledByDreamer,
        autofillInstructions: filledByDreamer ? autofillInstructions : null,
        dreamerFlow: filledByDreamer ? dreamerFlow : null,
        systemFieldReplacement: replacement
      };

      // Marked before the handoff: a failed save may already have published the draft
      // as a live question (and the server may have moved answers onto it), so closing
      // after a failure must never delete it — a leaked hidden draft is recoverable,
      // a deleted question holding migrated answers is not.
      markSaved();
      await onSave?.(updatedFieldData);
      handleClose();
    } catch (error) {
      console.error("Failed to update workflow field:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldLabelFields: FormFieldNewType[] = [
    {
      name: "fieldLabel",
      type: "text" as const,
      label: t(`${TRANSLATION_PREFIX}.fields.fieldName`),
      required: true,
      fieldActionProps: {
        placeholder: t(`${TRANSLATION_PREFIX}.placeholders.addFieldLabel`),
        value: fieldLabel,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          setFieldLabel(e.target.value);
          setIsFormValid(e.target.value.trim().length > 0);
        }
      }
    }
  ];

  if (showHelpText) {
    fieldLabelFields.push({
      name: "fieldHelpText",
      type: "text" as const,
      label: t(`${TRANSLATION_PREFIX}.fields.fieldHelpText`),
      required: false,
      fieldActionProps: {
        placeholder: "",
        value: fieldHelpText,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          setFieldHelpText(e.target.value);
        }
      }
    });
  }

  const formSections: FormSectionProps[] = [
    {
      id: "field-label",
      divider: true,

      dividerClassName: "my-2",
      fields: fieldLabelFields,
      bottomCustomComponent: !showHelpText ? (
        <div className="flex justify-start mt-[-22px]">
          <AddActionButton
            label={t(`${TRANSLATION_PREFIX}.actions.addHelpText`)}
            onClick={() => setShowHelpText(true)}
            textVariant="body"
          />
        </div>
      ) : undefined
    },
    {
      id: "field-type",
      divider: true,

      dividerClassName: "my-2",
      fields: [
        {
          name: "fieldType",
          type: "singleSelect" as const,
          label: t(`${TRANSLATION_PREFIX}.fields.fieldType`),
          required: true,
          defaultValue: defaultFieldDisplayType,
          hintText:
            workflowType === QuestionnaireType.SIMPLE ||
            workflowType === QuestionnaireType.SCORE
              ? t(
                  `settings.deals.workflow.addSimpleField.helpTexts.fieldTypeRestriction`
                )
              : "",
          fieldActionProps: {
            fieldName: "fieldType",
            options: generateFieldDisplayTypeOptions(t, true, true),
            placeholder: t(
              `${TRANSLATION_PREFIX}.placeholders.selectFieldType`
            ),
            renderIcon: (value: string | number) =>
              getFieldDisplayTypeIcon(value as FieldDisplayType, "secondary"),
            disabled: true, // Always disabled for edit mode
            value: defaultFieldDisplayType
          }
        }
      ] as FormFieldNewType[]
    }
  ];

  if (!fieldData) {
    return null;
  }

  const hasEdits = workflowFieldHasEdits(fieldData, {
    label: fieldLabel,
    helpText: fieldHelpText,
    required: isRequiredForPass,
    filledByDreamer,
    instructions: autofillInstructions,
    dreamerFlow
  });

  return (
    <>
      <Drawer
        open={isOpen}
        onOpenChange={onOpenChange}
        direction="right"
        modal={false}
        dismissible={false}
      >
        <AppDrawerContent isSettingsDrawer>
          <DrawerHeader>
            <DrawerTitle>
              <CustomDrawerTitle title={t(`${TRANSLATION_PREFIX}.title`)} />
            </DrawerTitle>
            <IconButton
              data-test="close-edit-simple-workflow-field-drawer"
              onClick={handleClose}
              variant="iconNoBg"
              size="md"
            >
              <Close />
            </IconButton>
          </DrawerHeader>

          <div className="p-5 w-full overflow-y-auto max-h-[calc(100vh-200px)]">
            <FormNew
              sections={formSections}
              isFormValid={handleFormValid}
              onSubmit={handleSubmit}
              formRef={formRef}
              onSetValue={() => {}}
            />

            {showAutofill ? (
              <>
                <div className="w-full border-t-[0.5px] border-border-border1 my-6" />
                {/* Remount per field: the section's question id, instructions and refs are
                    all per-field state, and the drawer instance survives field switches. */}
                <AIAutofillSection
                  key={fieldData?.id}
                  fieldName={fieldLabel}
                  fieldType={
                    fieldData?.config?.type || fieldData?.displayType || ""
                  }
                  helpText={fieldHelpText}
                  questionnaireId={questionnaireId}
                  enabled={filledByDreamer}
                  onEnabledChange={setFilledByDreamer}
                  onInstructionsChange={setAutofillInstructions}
                  onQuestionIdChange={onAutofillQuestionIdChange}
                  onDreamerFlowChange={setDreamerFlow}
                  onSaveBlockedChange={setAutofillSaveBlocked}
                  promptOwner={promptOwner}
                  translationPrefix={AUTOFILL_TRANSLATION_KEY}
                  initialQuestionId={fieldData?.id}
                  initialFieldName={fieldData?.label}
                  isExistingAutofill={fieldData?.filledByDreamer ?? false}
                  initialDreamerFlow={fieldData?.dreamerFlow ?? null}
                  className=""
                />
                <div className="w-full border-t-[0.5px] border-border-border1 my-6" />
              </>
            ) : null}

            <WorkflowRequiredFieldSection
              isRequiredForPass={isRequiredForPass}
              onRequiredForPassChange={setIsRequiredForPass}
              translationPrefix={
                workflowType === QuestionnaireType.MAPPING
                  ? "settings.deals.workflow.requiredFieldSection"
                  : `${TRANSLATION_PREFIX}.requiredForPass`
              }
              className={showAutofill ? "" : "mt-6"}
            />
          </div>

          <DrawerFooter>
            <div className="flex justify-between gap-2 w-full p-4 border-t-[0.5px] border-border-border1">
              <Button
                variant="tertiary"
                onClick={handleDeleteClick}
                className="h-9"
                destructive
              >
                {t(`${TRANSLATION_PREFIX}.actions.delete`)}
              </Button>
              <div className="flex gap-2 ml-auto">
                <Button
                  variant="secondaryNoBg"
                  onClick={handleClose}
                  className="h-9"
                >
                  {t(`${TRANSLATION_PREFIX}.actions.cancel`)}
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={
                    !isFormValid ||
                    !hasEdits ||
                    isSubmitting ||
                    autofillSaveBlocked
                  }
                  className="h-9"
                >
                  {t(`${TRANSLATION_PREFIX}.actions.saveChanges`)}
                </Button>
              </div>
            </div>
          </DrawerFooter>
        </AppDrawerContent>
      </Drawer>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        title={t(`${TRANSLATION_KEY_TABS}.fieldManagement.deleteField.title`)}
        confirmationText={t(
          `${TRANSLATION_KEY_TABS}.fieldManagement.deleteField.enhancedConfirmationText`
        )}
        confirmationTextVariant="body"
        handleSubmit={handleConfirmDelete}
        handleCancel={handleCancelDelete}
        submitButtonText={t(
          `${TRANSLATION_KEY_TABS}.fieldManagement.deleteField.submitButton`
        )}
        cancelButtonText={t("cancel")}
        checkboxLabel={t(
          `${TRANSLATION_KEY_TABS}.fieldManagement.deleteField.checkboxLabel`
        )}
        isCheckboxRequired={true}
        onCheckboxChange={setIsDeleteConfirmed}
        isCheckboxChecked={isDeleteConfirmed}
      />
    </>
  );
};
